export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

export const formatUTCTime = (utcTimestamp: string) => {
  const date = new Date(utcTimestamp);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
};

// Format Unix timestamp (seconds or milliseconds) to UTC time string
export const formatUnixTimestampToUTC = (unixTimestamp: number): string => {
  // Check if timestamp is in seconds (10 digits) or milliseconds (13 digits)
  // Convert to milliseconds if needed
  const timestampMs = unixTimestamp.toString().length <= 10 ? unixTimestamp * 1000 : unixTimestamp;

  const date = new Date(timestampMs);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
};

// Format Unix timestamp (seconds or milliseconds) to local time string
export const formatUnixTimestampToLocal = (unixTimestamp: number): string => {
  // Check if timestamp is in seconds (10 digits) or milliseconds (13 digits)
  // Convert to milliseconds if needed
  const timestampMs = unixTimestamp.toString().length <= 10 ? unixTimestamp * 1000 : unixTimestamp;

  const date = new Date(timestampMs);

  // Get local time components
  const localYear = date.getFullYear();
  const localMonth = String(date.getMonth() + 1).padStart(2, '0');
  const localDay = String(date.getDate()).padStart(2, '0');
  const localHours = String(date.getHours()).padStart(2, '0');
  const localMinutes = String(date.getMinutes()).padStart(2, '0');
  const localSeconds = String(date.getSeconds()).padStart(2, '0');

  // Get timezone abbreviation using Intl.DateTimeFormat
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneAbbr =
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || 'Local';

  return `${localYear}-${localMonth}-${localDay} ${localHours}:${localMinutes}:${localSeconds} ${timeZoneAbbr}`;
};

export const convertToLocalTime = (utcTimestamp: string): string => {
  // Create date object from UTC timestamp
  // Only add 'Z' if it's not already present
  const dateString = utcTimestamp.endsWith('Z') ? utcTimestamp : utcTimestamp + 'Z';
  const date = new Date(dateString);

  // Get local time components
  const localYear = date.getFullYear();
  const localMonth = String(date.getMonth() + 1).padStart(2, '0');
  const localDay = String(date.getDate()).padStart(2, '0');
  const localHours = String(date.getHours()).padStart(2, '0');
  const localMinutes = String(date.getMinutes()).padStart(2, '0');

  // Get timezone abbreviation using Intl.DateTimeFormat
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneAbbr =
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || 'UTC';

  return `${localYear}-${localMonth}-${localDay} ${localHours}:${localMinutes} ${timeZoneAbbr}`;
};

export const convertToLocalDate = (utcTimestamp: string): string => {
  // Create date object from UTC timestamp
  // Only add 'Z' if it's not already present
  const dateString = utcTimestamp.endsWith('Z') ? utcTimestamp : utcTimestamp + 'Z';
  const date = new Date(dateString);

  // Get local date components
  const localYear = date.getFullYear();
  const localMonth = String(date.getMonth() + 1).padStart(2, '0');
  const localDay = String(date.getDate()).padStart(2, '0');

  return `${localYear}-${localMonth}-${localDay}`;
};

// Format date to YYYY-MM-DD
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Add `days` to a YYYY-MM-DD string and return YYYY-MM-DD. UTC-based so it never
// drifts across DST/local-offset boundaries.
export const addDays = (yyyyMmDd: string, days: number): string => {
  const date = new Date(`${yyyyMmDd}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

// SGT (UTC+8, no DST) calendar day -> UTC instant at the start of that day.
// SGT midnight = the previous day 16:00Z (e.g. 2026-06-12 -> 2026-06-11T16:00:00.000Z).
export const sgtDayStartUtc = (yyyyMmDd: string): string =>
  new Date(`${yyyyMmDd}T00:00:00.000+08:00`).toISOString();

// Convert an inclusive SGT calendar range [startDay, endDay] (both YYYY-MM-DD) into the
// canonical instant bounds the backend expects: a half-open [from, to) where `to` is
// end-EXCLUSIVE. To include the last SGT day we push `to` to the start of the next day,
// otherwise orders on `endDay` are silently dropped.
export const sgtRangeToInstantBounds = (
  startDay: string,
  endDay: string,
): { from: string; to: string } => ({
  from: sgtDayStartUtc(startDay),
  to: sgtDayStartUtc(addDays(endDay, 1)),
});

const HAS_TIME_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

export const formatUtcDateTimeToSGT = (utcTimestamp?: string | null): string => {
  if (!utcTimestamp) {
    return '';
  }

  const date = new Date(HAS_TIME_ZONE.test(utcTimestamp) ? utcTimestamp : `${utcTimestamp}Z`);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-GB', { timeZone: 'Asia/Singapore', hour12: false }).replace(',', '');
};


/** A blank cell cannot be told apart from a value that is genuinely absent, so both read as the dash. */
export const formatWarehouseDateTime = (utcTimestamp?: string | null): string =>
  formatUtcDateTimeToSGT(utcTimestamp) || '—';

// Format ISO date string to YYYY-MM-DD HH:mm (UTC)
export const formatISODateTimeToUTC = (isoDateString: string | undefined): string => {
  if (!isoDateString) {
    return '';
  }

  const date = new Date(isoDateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes} (UTC)`;
};

// Get months between two dates (inclusive)
export const getMonthsBetweenDates = (startDate: string, endDate: string): string[] => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const months: string[] = [];
  const currentDate = new Date(start);

  // Set to first day of month to ensure proper month counting
  currentDate.setDate(1);
  end.setDate(1);

  while (currentDate <= end) {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);

    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return months;
};

// Get number of months between two dates (inclusive)
export const getNumberOfMonths = (startDate: string, endDate: string): number => {
  const months = getMonthsBetweenDates(startDate, endDate);
  return months.length;
};

// Current calendar day in SGT (UTC+8, no DST) as YYYY-MM-DD, independent of the browser's
// timezone. Operators run from different regions (e.g. KST), but the OMS day boundary is SGT.
export const sgtTodayYYYYMMDD = (now: Date = new Date()): string =>
  new Date(now.getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);

// Get default date range for orders (from 2 months ago to end of current month), anchored to
// the current SGT calendar month so the default is the same regardless of where the operator is.
export const getDefaultOrderDateRange = (now: Date = new Date()): { startDate: string; endDate: string } => {
  const sgtToday = sgtTodayYYYYMMDD(now); // YYYY-MM-DD
  const year = Number(sgtToday.slice(0, 4));
  const month = Number(sgtToday.slice(5, 7)); // 1-12

  // Start: first day of the month two months before the current SGT month.
  // End: last day of the current SGT month (day 0 of the next month).
  // Built with UTC so the result never shifts with the browser's local offset.
  const startDate = new Date(Date.UTC(year, month - 1 - 2, 1));
  const endDate = new Date(Date.UTC(year, month, 0));

  return {
    startDate: startDate.toISOString().slice(0, 10),
    endDate: endDate.toISOString().slice(0, 10),
  };
};

// Format ISO datetime string to a user-friendly format
export const formatDateTime = (isoDateString: string): string => {
  try {
    const date = new Date(isoDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return isoDateString; // Return original string if parsing fails
  }
};
